/* eslint-disable quotes */
import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Card,
  Typography,
  Badge,
  Table,
  Row,
  Col,
  Statistic,
  Avatar,
  Progress,
  Divider,
  Space,
  Rate,
  Alert,
  Timeline,
  Tooltip,
  Drawer,
  Button,
  ConfigProvider,
  Tag,
} from 'antd';
import {
  TrophyOutlined,
  TeamOutlined,
  StarOutlined,
  FireOutlined,
  HistoryOutlined,
  BulbOutlined,
  HomeOutlined,
  CalendarOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  MenuOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';

const { Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;

// ─── DATA ────────────────────────────────────────────────────────────────────

const GROUPS = [
  { id: 'A', teams: ['Mexico', 'South Africa', 'South Korea', 'Czechia'], host: 'Mexico' },
  { id: 'B', teams: ['Canada', 'Bosnia & Herzegovina', 'Qatar', 'Switzerland'], host: 'Canada' },
  { id: 'C', teams: ['USA', 'Türkiye', 'Australia', 'Paraguay'], host: 'USA' },
  { id: 'D', teams: ['Spain', 'Brazil', 'Japan', 'Morocco'], host: 'USA' },
  { id: 'E', teams: ['Germany', 'Ecuador', 'Ivory Coast', 'Curaçao'], host: 'USA' },
  { id: 'F', teams: ['England', 'Netherlands', 'Venezuela', 'New Zealand'], host: 'USA' },
  { id: 'G', teams: ['Iran', 'Egypt', 'New Zealand', 'Belgium'], host: 'USA' },
  { id: 'H', teams: ['Portugal', 'Uruguay', 'Serbia', 'Saudi Arabia'], host: 'USA' },
  { id: 'I', teams: ['France', 'Norway', 'Senegal', 'Iraq'], host: 'USA' },
  { id: 'J', teams: ['Argentina', 'Algeria', 'Austria', 'Jordan'], host: 'USA' },
  { id: 'K', teams: ['Portugal', 'Congo DR', 'Uzbekistan', 'Colombia'], host: 'USA' },
  { id: 'L', teams: ['Croatia', 'Sweden', 'Ghana', 'Panama'], host: 'USA' },
];

const STADIUMS = [
  { name: 'MetLife Stadium', city: 'New York / New Jersey', country: 'USA', capacity: 82500, notable: 'Hosts the Final on July 19', flag: '🇺🇸' },
  { name: 'AT&T Stadium', city: 'Dallas', country: 'USA', capacity: 94000, notable: 'Largest capacity — hosts a Semi-Final', flag: '🇺🇸' },
  { name: 'Mercedes-Benz Stadium', city: 'Atlanta', country: 'USA', capacity: 75000, notable: 'Hosts a Semi-Final', flag: '🇺🇸' },
  { name: 'SoFi Stadium', city: 'Los Angeles', country: 'USA', capacity: 70000, notable: 'Hosts a Quarter-Final', flag: '🇺🇸' },
  { name: 'Estadio Azteca', city: 'Mexico City', country: 'Mexico', capacity: 83000, notable: 'Opening Match venue — 3rd World Cup here', flag: '🇲🇽' },
  { name: 'Hard Rock Stadium', city: 'Miami', country: 'USA', capacity: 65000, notable: 'Hosted Copa América 2016 Final', flag: '🇺🇸' },
  { name: 'Lumen Field', city: 'Seattle', country: 'USA', capacity: 69000, notable: 'USA Group Stage venue', flag: '🇺🇸' },
  { name: 'BC Place', city: 'Vancouver', country: 'Canada', capacity: 54500, notable: 'Canadas scenic coastal venue', flag: '🇨🇦' },
  { name: 'BMO Field', city: 'Toronto', country: 'Canada', capacity: 45500, notable: 'Expanded for the tournament', flag: '🇨🇦' },
  { name: 'Estadio Akron', city: 'Guadalajara', country: 'Mexico', capacity: 48000, notable: 'Group stage venue', flag: '🇲🇽' },
  { name: 'Estadio BBVA', city: 'Monterrey', country: 'Mexico', capacity: 53500, notable: 'Group stage venue', flag: '🇲🇽' },
  { name: 'Gillette Stadium', city: 'Boston', country: 'USA', capacity: 65878, notable: 'Undergoing pre-tournament renovation', flag: '🇺🇸' },
  { name: 'Lincoln Financial Field', city: 'Philadelphia', country: 'USA', capacity: 69796, notable: 'Group stage venue', flag: '🇺🇸' },
  { name: 'Arrowhead Stadium', city: 'Kansas City', country: 'USA', capacity: 76416, notable: 'Group stage venue', flag: '🇺🇸' },
  { name: 'NRG Stadium', city: 'Houston', country: 'USA', capacity: 72220, notable: 'Indoor retractable-roof venue', flag: '🇺🇸' },
  { name: 'Levi Stadium', city: 'San Francisco', country: 'USA', capacity: 68500, notable: 'Group stage venue', flag: '🇺🇸' },
];

const KEY_PLAYERS = [
  { name: 'Lionel Messi', country: 'Argentina', age: 38, club: 'Inter Miami', position: 'Forward', goals: 13, caps: 183, highlight: 'Defending champion — his final World Cup', rating: 5, flag: '🇦🇷', color: '#4096ff' },
  { name: 'Cristiano Ronaldo', country: 'Portugal', age: 41, club: 'Al-Nassr', position: 'Forward', goals: 14, caps: 212, highlight: 'Record 6th World Cup — last chance for the trophy', rating: 5, flag: '🇵🇹', color: '#ff4d4f' },
  { name: 'Kylian Mbappé', country: 'France', age: 27, club: 'Real Madrid', position: 'Forward', goals: 12, caps: 82, highlight: 'Prime years — leading favourite to win the Golden Boot', rating: 5, flag: '🇫🇷', color: '#722ed1' },
  { name: 'Erling Haaland', country: 'Norway', age: 25, club: 'Manchester City', position: 'Striker', goals: 16, caps: 41, highlight: 'First major tournament — 16 goals in qualifying', rating: 5, flag: '🇳🇴', color: '#52c41a' },
  { name: 'Jude Bellingham', country: 'England', age: 22, club: 'Real Madrid', position: 'Midfielder', goals: 9, caps: 52, highlight: 'England creative engine — golden generation', rating: 4, flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', color: '#fa8c16' },
  { name: 'Vinicius Jr.', country: 'Brazil', age: 25, club: 'Real Madrid', position: 'Winger', goals: 8, caps: 49, highlight: 'Brazil talisman — electric pace and flair', rating: 4, flag: '🇧🇷', color: '#13c2c2' },
  { name: 'Lamine Yamal', country: 'Spain', age: 18, club: 'Barcelona', position: 'Winger', goals: 7, caps: 24, highlight: 'Euro 2024 winner — youngest superstar in football', rating: 4, flag: '🇪🇸', color: '#eb2f96' },
  { name: 'Pedri', country: 'Spain', age: 23, club: 'Barcelona', position: 'Midfielder', goals: 5, caps: 48, highlight: 'World-class orchestrator for defending Euro champions', rating: 4, flag: '🇪🇸', color: '#eb2f96' },
];

const KEY_TEAMS = [
  { name: 'Argentina', flag: '🇦🇷', titles: 3, coach: 'Lionel Scaloni', strength: 95, style: 'Technical & Tactical', ranking: 1, desc: 'Defending World Cup and back-to-back Copa América champions. Messis farewell campaign makes them the sentimental and tactical favourites.', color: '#4096ff' },
  { name: 'France', flag: '🇫🇷', titles: 2, coach: 'Didier Deschamps', strength: 94, style: 'Counter-attack & Flair', ranking: 2, desc: '2022 finalists with Mbappé at his absolute peak. Arguably the deepest squad at the tournament.', color: '#722ed1' },
  { name: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', titles: 1, coach: 'Gareth Southgate', strength: 88, style: 'Press & Direct', ranking: 4, desc: 'Euro 2024 finalists with a golden generation: Kane, Bellingham, Foden, Saka. Hunger to end a 60-year drought.', color: '#fa8c16' },
  { name: 'Brazil', flag: '🇧🇷', titles: 5, coach: 'Carlo Ancelotti', strength: 90, style: 'Attacking & Expressive', ranking: 5, desc: 'Most decorated nation in World Cup history. Vinicius Jr., Rodrygo and a new generation seek to end a 24-year wait.', color: '#52c41a' },
  { name: 'Germany', flag: '🇩🇪', titles: 4, coach: 'Julian Nagelsmann', strength: 87, style: 'High Press & Structure', ranking: 3, desc: 'Euro 2024 hosts resurgence with Musiala and Wirtz. Four-time champions rebuilt and dangerous.', color: '#faad14' },
  { name: 'Spain', flag: '🇪🇸', titles: 1, coach: 'Luis de la Fuente', strength: 89, style: 'Possession & Tiki-Taka', ranking: 6, desc: 'Euro 2024 winners with Yamal and Pedri. The most technically gifted team in the tournament.', color: '#ff4d4f' },
];

const RIVALRIES = [
  { team1: 'Argentina 🇦🇷', team2: 'Brazil 🇧🇷', name: 'El Superclásico de las Américas', desc: 'The fiercest rivalry in South American football. Every meeting is an occasion of continental pride. Their 2022 semi-final clash would be one of the all-time great fixtures if it materialises.', intensity: 5 },
  { team1: 'England 🏴󠁧󠁢󠁥󠁮󠁧󠁿', team2: 'Germany 🇩🇪', name: 'The Great Duel', desc: 'Decades of iconic World Cup battles — 1966 Final, 1970 quarter-final, 1990 semi-final shootout, 2010 round of 16. History is always the backdrop.', intensity: 5 },
  { team1: 'Spain 🇪🇸', team2: 'Portugal 🇵🇹', name: 'Iberian Derby', desc: 'Neighbouring nations who share a peninsula but not trophies. The 2018 group stage draw produced one of the greatest World Cup games ever (3-3).', intensity: 4 },
  { team1: 'France 🇫🇷', team2: 'Argentina 🇦🇷', name: 'The Modern Rivalry', desc: '2018 Round of 16 (4-3 thriller), 2022 Final (arguably the greatest ever, Argentina winning on penalties). Set to define this era.', intensity: 5 },
  { team1: 'USA 🇺🇸', team2: 'Mexico 🇲🇽', name: 'El Clásico CONCACAF', desc: 'North Americas great football rivalry, now playing on the biggest stage as co-hosts. A group meeting in 2026 would be seismic.', intensity: 4 },
  { team1: 'Netherlands 🇳🇱', team2: 'Germany 🇩🇪', name: 'The Neighbour War', desc: 'Hollands 1974 World Cup final loss to West Germany seeded decades of rivalry. Their clashes are always technically electric.', intensity: 4 },
];

const HISTORY = [
  { year: 1930, host: 'Uruguay', champion: 'Uruguay', runner: 'Argentina', goals: 70, teams: 13 },
  { year: 1934, host: 'Italy', champion: 'Italy', runner: 'Czechoslovakia', goals: 70, teams: 16 },
  { year: 1938, host: 'France', champion: 'Italy', runner: 'Hungary', goals: 84, teams: 15 },
  { year: 1950, host: 'Brazil', champion: 'Uruguay', runner: 'Brazil', goals: 88, teams: 13 },
  { year: 1954, host: 'Switzerland', champion: 'West Germany', runner: 'Hungary', goals: 140, teams: 16 },
  { year: 1958, host: 'Sweden', champion: 'Brazil', runner: 'Sweden', goals: 126, teams: 16 },
  { year: 1962, host: 'Chile', champion: 'Brazil', runner: 'Czechoslovakia', goals: 89, teams: 16 },
  { year: 1966, host: 'England', champion: 'England', runner: 'West Germany', goals: 89, teams: 16 },
  { year: 1970, host: 'Mexico', champion: 'Brazil', runner: 'Italy', goals: 95, teams: 16 },
  { year: 1974, host: 'West Germany', champion: 'West Germany', runner: 'Netherlands', goals: 97, teams: 16 },
  { year: 1978, host: 'Argentina', champion: 'Argentina', runner: 'Netherlands', goals: 102, teams: 16 },
  { year: 1982, host: 'Spain', champion: 'Italy', runner: 'West Germany', goals: 146, teams: 24 },
  { year: 1986, host: 'Mexico', champion: 'Argentina', runner: 'West Germany', goals: 132, teams: 24 },
  { year: 1990, host: 'Italy', champion: 'West Germany', runner: 'Argentina', goals: 115, teams: 24 },
  { year: 1994, host: 'USA', champion: 'Brazil', runner: 'Italy', goals: 141, teams: 24 },
  { year: 1998, host: 'France', champion: 'France', runner: 'Brazil', goals: 171, teams: 32 },
  { year: 2002, host: 'Japan/Korea', champion: 'Brazil', runner: 'Germany', goals: 161, teams: 32 },
  { year: 2006, host: 'Germany', champion: 'Italy', runner: 'France', goals: 147, teams: 32 },
  { year: 2010, host: 'South Africa', champion: 'Spain', runner: 'Netherlands', goals: 145, teams: 32 },
  { year: 2014, host: 'Brazil', champion: 'Germany', runner: 'Argentina', goals: 171, teams: 32 },
  { year: 2018, host: 'Russia', champion: 'France', runner: 'Croatia', goals: 169, teams: 32 },
  { year: 2022, host: 'Qatar', champion: 'Argentina', runner: 'France', goals: 172, teams: 32 },
  { year: 2026, host: 'USA/Mexico/Canada', champion: 'TBD', runner: 'TBD', goals: null, teams: 48 },
];

const CHAMPIONS_STATS = [
  { country: 'Brazil 🇧🇷', titles: 5, years: '1958, 1962, 1970, 1994, 2002' },
  { country: 'Germany 🇩🇪', titles: 4, years: '1954, 1974, 1990, 2014' },
  { country: 'Italy 🇮🇹', titles: 4, years: '1934, 1938, 1966, 2006' },
  { country: 'Argentina 🇦🇷', titles: 3, years: '1978, 1986, 2022' },
  { country: 'France 🇫🇷', titles: 2, years: '1998, 2018' },
  { country: 'Uruguay 🇺🇾', titles: 2, years: '1930, 1950' },
  { country: 'England 🏴󠁧󠁢󠁥󠁮󠁧󠁿', titles: 1, years: '1966' },
  { country: 'Spain 🇪🇸', titles: 1, years: '2010' },
];

const FACTS = [
  { icon: '🌍', fact: 'First World Cup co-hosted by three nations simultaneously', category: 'History' },
  { icon: '⚽', fact: '104 total matches — nearly 63% more than the 2022 edition (64 games)', category: 'Format' },
  { icon: '🏟️', fact: '16 stadiums used — the most since Japan/Korea 2002', category: 'Venues' },
  { icon: '🌎', fact: '48 nations compete, up from 32 — including first-timers Uzbekistan, Curaçao and Haiti', category: 'Teams' },
  { icon: '🏆', fact: 'Argentina is the first back-to-back title defence attempt since Brazil 1958-62', category: 'Records' },
  { icon: '📺', fact: 'The 2022 final drew 1.5 billion viewers — 2026 is projected to surpass 2 billion', category: 'Media' },
  { icon: '⏱️', fact: '39-day tournament window — the longest in World Cup history', category: 'Format' },
  { icon: '🎵', fact: 'Coldplay will headline the MetLife Stadium half-time show on July 19', category: 'Entertainment' },
  { icon: '⚽', fact: 'The official ball Trionda (three waves) honours all three host nations', category: 'Equipment' },
  { icon: '👴', fact: 'Cristiano Ronaldo at 41 will be the oldest outfield player ever to score at a World Cup if he nets in 2026', category: 'Records' },
  { icon: '🇲🇽', fact: 'The Estadio Azteca in Mexico City has hosted three separate World Cup opening matches (1970, 1986, 2026)', category: 'History' },
  { icon: '💰', fact: 'The total prize fund exceeds $1 billion — a record for any sporting event', category: 'Finance' },
];

const STAGES = [
  { stage: 'Group Stage', matches: 72, teams: 48, dates: 'June 11 – July 2', desc: '12 groups of 4; top 2 from each group + 8 best 3rd-place sides advance' },
  { stage: 'Round of 32', matches: 16, teams: 32, dates: 'July 4 – 8', desc: 'Single-leg knockout — first time in World Cup history' },
  { stage: 'Round of 16', matches: 8, teams: 16, dates: 'July 9 – 11', desc: 'Classic knockout bracket continues' },
  { stage: 'Quarter-Finals', matches: 4, teams: 8, dates: 'July 13 – 14', desc: 'At Dallas, Los Angeles, Miami and Kansas City' },
  { stage: 'Semi-Finals', matches: 2, teams: 4, dates: 'July 15 – 16', desc: 'AT&T Stadium Dallas & Mercedes-Benz Stadium Atlanta' },
  { stage: 'Third Place Play-off', matches: 1, teams: 2, dates: 'July 18', desc: 'Miami' },
  { stage: 'Final', matches: 1, teams: 2, dates: 'July 19', desc: 'MetLife Stadium, New York/New Jersey' },
];

// Dark theme colors
const DARK_BG = '#212121';
const DARKER_BG = '#1a1a1a';
const CARD_BG = '#2c2c2c';
const BORDER_COLOR = '#3a3a3a';
const TEXT_PRIMARY = '#f0f0f0';
const TEXT_SECONDARY = '#b0b0b0';
const ACCENT_COLOR = '#ffb347';

/* -------------------- REUSABLE SMALL COMPONENT -------------------- */
const SectionTitle = ({ icon, title }: any) => (
  <Title level={4} style={{ marginBottom: 12, color: TEXT_PRIMARY }}>
    {icon} {title}
  </Title>
);

// Add this helper function before your OverviewSection
const getTimeRemaining = (targetDate: Date) => {
  const total = targetDate.getTime() - new Date().getTime();
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  
  return {
    total,
    days,
    hours,
    minutes,
    seconds,
    isLive: total <= 0
  };
};

// Countdown Timer Component
const CountdownTimer = () => {
  // Tournament start date: June 11, 2026
  const startDate = new Date(2026, 5, 11, 16, 0, 0); // June 11, 2026, 16:00 ET
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining(startDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getTimeRemaining(startDate));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (timeRemaining.isLive) {
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ 
          display: 'inline-flex', 
          gap: 16, 
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Tag icon={<TrophyOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
            Tournament Live Now!
          </Tag>
          <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
            🔴 Watch matches live across USA, Mexico & Canada
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ 
        display: 'flex', 
        gap: 20, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 28, 
            fontWeight: 'bold', 
            color: '#ffb347',
            background: '#1a1a1a',
            padding: '8px 16px',
            borderRadius: 8,
            minWidth: 70
          }}>
            {String(timeRemaining.days).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 12, color: '#b0b0b0', marginTop: 4 }}>Days</div>
        </div>
        <div style={{ fontSize: 24, color: '#ffb347' }}>:</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 28, 
            fontWeight: 'bold', 
            color: '#ffb347',
            background: '#1a1a1a',
            padding: '8px 16px',
            borderRadius: 8,
            minWidth: 70
          }}>
            {String(timeRemaining.hours).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 12, color: '#b0b0b0', marginTop: 4 }}>Hours</div>
        </div>
        <div style={{ fontSize: 24, color: '#ffb347' }}>:</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 28, 
            fontWeight: 'bold', 
            color: '#ffb347',
            background: '#1a1a1a',
            padding: '8px 16px',
            borderRadius: 8,
            minWidth: 70
          }}>
            {String(timeRemaining.minutes).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 12, color: '#b0b0b0', marginTop: 4 }}>Minutes</div>
        </div>
        <div style={{ fontSize: 24, color: '#ffb347' }}>:</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 28, 
            fontWeight: 'bold', 
            color: '#ffb347',
            background: '#1a1a1a',
            padding: '8px 16px',
            borderRadius: 8,
            minWidth: 70
          }}>
            {String(timeRemaining.seconds).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 12, color: '#b0b0b0', marginTop: 4 }}>Seconds</div>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <Text style={{ color: '#b0b0b0', fontSize: 12 }}>
          🏆 Opening Match: Mexico vs South Africa • June 11, 2026 • Estadio Azteca
        </Text>
      </div>
    </div>
  );
};

/* -------------------- SECTIONS -------------------- */

const OverviewSection = () => (
  <>
    <Alert
      message={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 16 }}>
            <TrophyOutlined style={{ marginRight: 8, color: '#ffb347' }} />
            FIFA World Cup 2026
          </span>
          <Tag color="processing" style={{ fontSize: 12, background: '#ffb347', color: '#1a1a1a', border: 'none' }}>
            🇺🇸 🇲🇽 🇨🇦
          </Tag>
        </div>
      }
      description={
        <div>
          <div style={{ marginBottom: 8 }}>
            The 23rd edition is coming live across USA, Mexico and Canada.
          </div>
          <CountdownTimer />
        </div>
      }
      type="info"
      showIcon
      style={{ marginBottom: 24, backgroundColor: '#2a3a4a', borderColor: '#3a6a7a' }}
    />

    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {[
        ["Teams", 48],
        ["Matches", 104],
        ["Cities", 16],
        ["Countries", 3],
        ["Days", 39],
        ["Editions", 22],
      ].map(([label, value]) => (
        <Col xs={12} sm={8} md={4} key={label}>
          <Card style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Statistic 
              title={<span style={{ color: TEXT_SECONDARY }}>{label}</span>} 
              value={value as number}
              valueStyle={{ color: ACCENT_COLOR }}
            />
          </Card>
        </Col>
      ))}
    </Row>

    <Card 
      title={<span style={{ color: TEXT_PRIMARY }}><TrophyOutlined style={{ color: ACCENT_COLOR }} /> About the Tournament</span>}
      style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Main Description */}
        <Paragraph style={{ color: TEXT_SECONDARY, fontSize: 16 }}>
          The <strong style={{ color: ACCENT_COLOR }}>2026 FIFA World Cup</strong> marks a historic milestone as the first edition to feature 
          <strong style={{ color: ACCENT_COLOR }}> 48 teams</strong> and the first to be co-hosted by three nations: 
          <strong style={{ color: ACCENT_COLOR }}> the United States, Mexico, and Canada</strong>. This expanded format promises unprecedented 
          global representation and football drama across North America.
        </Paragraph>

        <Divider style={{ borderColor: BORDER_COLOR, margin: '8px 0' }} />

        {/* Key Highlights Grid */}
        <Title level={5} style={{ color: TEXT_PRIMARY, marginTop: 8 }}>🏆 Tournament Highlights</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <div style={{ backgroundColor: '#323232', padding: '12px', borderRadius: 8 }}>
              <Text strong style={{ color: ACCENT_COLOR }}>📅 Duration</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 4, marginBottom: 0 }}>
                39 days of football action (June 11 - July 19, 2026)
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ backgroundColor: '#323232', padding: '12px', borderRadius: 8 }}>
              <Text strong style={{ color: ACCENT_COLOR }}>🎯 New Format</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 4, marginBottom: 0 }}>
                12 groups of 4 teams → Round of 32 (new) → knockout stage
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ backgroundColor: '#323232', padding: '12px', borderRadius: 8 }}>
              <Text strong style={{ color: ACCENT_COLOR }}>🏟️ Host Cities</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 4, marginBottom: 0 }}>
                16 venues across 3 countries: 11 in USA, 3 in Mexico, 2 in Canada
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ backgroundColor: '#323232', padding: '12px', borderRadius: 8 }}>
              <Text strong style={{ color: ACCENT_COLOR }}>💰 Prize Money</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 4, marginBottom: 0 }}>
                Record $1 billion+ prize fund for participating nations
              </Paragraph>
            </div>
          </Col>
        </Row>

        <Divider style={{ borderColor: BORDER_COLOR, margin: '8px 0' }} />

        {/* Host Nations Section */}
        <Title level={5} style={{ color: TEXT_PRIMARY, marginTop: 8 }}>🌎 Host Nations</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card size="small" style={{ backgroundColor: '#323232', borderColor: BORDER_COLOR, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🇺🇸</div>
              <Text strong style={{ color: TEXT_PRIMARY }}>United States</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                11 venues • Most matches including Final
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ backgroundColor: '#323232', borderColor: BORDER_COLOR, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🇲🇽</div>
              <Text strong style={{ color: TEXT_PRIMARY }}>Mexico</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                3 venues • Opening match at Estadio Azteca
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ backgroundColor: '#323232', borderColor: BORDER_COLOR, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🇨🇦</div>
              <Text strong style={{ color: TEXT_PRIMARY }}>Canada</Text>
              <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                2 venues • First-time World Cup host
              </Paragraph>
            </Card>
          </Col>
        </Row>

        <Divider style={{ borderColor: BORDER_COLOR, margin: '8px 0' }} />

        {/* Historic Significance */}
        <Title level={5} style={{ color: TEXT_PRIMARY, marginTop: 8 }}>📖 Historic Significance</Title>
        <ul style={{ color: TEXT_SECONDARY, paddingLeft: 20, marginBottom: 0 }}>
          <li>First World Cup with 48 teams, increasing from 32 (a 50% expansion)</li>
          <li>First triple-hosted World Cup in FIFA history</li>
          <li>Most matches ever played in a single World Cup (104 games)</li>
          <li>Mexico becomes first country to host three World Cups (1970, 1986, 2026)</li>
          <li>USA hosts its first World Cup since 1994 (record attendance of 3.6 million)</li>
          <li>Canada makes its debut as a World Cup host nation</li>
          <li>New knockout round introduced: Round of 32</li>
        </ul>

        <Divider style={{ borderColor: BORDER_COLOR, margin: '8px 0' }} />

        {/* Qualification Summary */}
        <Title level={5} style={{ color: TEXT_PRIMARY, marginTop: 8 }}>🌍 Qualification Summary</Title>
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• UEFA (Europe):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>16 teams</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• CAF (Africa):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>9 teams</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• CONMEBOL (S. America):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>6 teams</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• AFC (Asia):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>8 teams</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• CONCACAF (N. America):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>6 teams (including hosts)</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• OFC (Oceania):</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>1 team</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: TEXT_SECONDARY }}>• Play-off winners:</Text>
          </Col>
          <Col span={12}>
            <Text style={{ color: ACCENT_COLOR }}>2 teams</Text>
          </Col>
        </Row>

        <Divider style={{ borderColor: BORDER_COLOR, margin: '8px 0' }} />

        {/* Fun Facts */}
        <Title level={5} style={{ color: TEXT_PRIMARY, marginTop: 8 }}>✨ Did You Know?</Title>
        <Paragraph style={{ color: TEXT_SECONDARY, fontStyle: 'italic', marginBottom: 0 }}>
          The 2026 World Cup final at MetLife Stadium is expected to be viewed by over 
          2 billion people worldwide — making it one of the most-watched sporting events in history.
        </Paragraph>
      </Space>
    </Card>
  </>
);

const RivalriesSection = () => (
  <>
    <SectionTitle icon={<ThunderboltOutlined style={{ color: ACCENT_COLOR }} />} title="Rivalries" />
    <Row gutter={[16, 16]}>
      {RIVALRIES.map((r) => (
        <Col xs={24} md={12} key={r.name}>
          <Card style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Title level={5} style={{ color: TEXT_PRIMARY }}>{r.name}</Title>
            <Text style={{ color: TEXT_SECONDARY }}>{r.team1} vs {r.team2}</Text>
            <Paragraph style={{ color: TEXT_SECONDARY, marginTop: 8 }}>{r.desc}</Paragraph>
            <Rate disabled defaultValue={r.intensity} style={{ color: ACCENT_COLOR }} />
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const ChampionsSection = () => (
  <>
    <SectionTitle icon={<CrownOutlined style={{ color: ACCENT_COLOR }} />} title="Champions Stats" />
    {CHAMPIONS_STATS.map((c) => (
      <Card key={c.country} style={{ marginBottom: 10, backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
        <Text strong style={{ color: TEXT_PRIMARY }}>{c.country}</Text>
        <br />
        <Text style={{ color: TEXT_SECONDARY }}>{c.titles} titles</Text>
        <Tooltip title={c.years}>
          <Progress percent={(c.titles / 5) * 100} strokeColor={ACCENT_COLOR} />
        </Tooltip>
      </Card>
    ))}
  </>
);

const StagesSection = () => (
  <>
    <SectionTitle icon={<CalendarOutlined style={{ color: ACCENT_COLOR }} />} title="Tournament Stages" />
    <Timeline>
      {STAGES.map((s) => (
        <Timeline.Item key={s.stage}>
          <Card size="small" style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Text strong style={{ color: TEXT_PRIMARY }}>{s.stage}</Text>
            <br />
            <Text style={{ color: TEXT_SECONDARY }}>{s.dates}</Text>
            <br />
            <Text type="secondary" style={{ color: TEXT_SECONDARY }}>{s.desc}</Text>
          </Card>
        </Timeline.Item>
      ))}
    </Timeline>
  </>
);

const GroupsSection = () => (
  <>
    <SectionTitle icon={<TeamOutlined style={{ color: ACCENT_COLOR }} />} title="Groups" />
    <Row gutter={[12, 12]}>
      {GROUPS.map((g) => (
        <Col xs={24} sm={12} md={8} lg={6} key={g.id}>
          <Card 
            size="small" 
            title={<span style={{ color: TEXT_PRIMARY }}>Group {g.id}</span>}
            style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}
            headStyle={{ borderBottomColor: BORDER_COLOR }}
          >
            {g.teams.map((t, i) => (
              <div key={t} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                <Badge count={i + 1} style={{ backgroundColor: ACCENT_COLOR }} />
                <Text style={{ color: TEXT_SECONDARY }}>{t}</Text>
              </div>
            ))}
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const PlayersSection = () => (
  <>
    <SectionTitle icon={<StarOutlined style={{ color: ACCENT_COLOR }} />} title="Key Players" />
    <Row gutter={[16, 16]}>
      {KEY_PLAYERS.map((p) => (
        <Col xs={24} sm={12} lg={8} key={p.name}>
          <Card style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Space>
              <Avatar style={{ backgroundColor: p.color }}>{p.flag}</Avatar>
              <div>
                <Text strong style={{ color: TEXT_PRIMARY }}>{p.name}</Text>
                <br />
                <Text type="secondary" style={{ color: TEXT_SECONDARY }}>{p.country}</Text>
              </div>
            </Space>

            <Divider style={{ borderColor: BORDER_COLOR }} />

            <Paragraph style={{ color: TEXT_SECONDARY }}>{p.highlight}</Paragraph>
            <Rate disabled defaultValue={p.rating} style={{ color: ACCENT_COLOR }} />
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const TeamsSection = () => (
  <>
    <SectionTitle icon={<FireOutlined style={{ color: ACCENT_COLOR }} />} title="Top Teams" />
    <Row gutter={[16, 16]}>
      {KEY_TEAMS.map((t) => (
        <Col xs={24} sm={12} lg={8} key={t.name}>
          <Card style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Title level={5} style={{ color: TEXT_PRIMARY }}>{t.name}</Title>
            <Paragraph style={{ color: TEXT_SECONDARY }}>{t.desc}</Paragraph>
            <Progress percent={t.strength} strokeColor={ACCENT_COLOR} />
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const StadiumsSection = () => (
  <>
    <SectionTitle icon={<HomeOutlined style={{ color: ACCENT_COLOR }} />} title="Stadiums" />
    <Table
      dataSource={STADIUMS}
      rowKey="name"
      columns={[
        { title: "Stadium", dataIndex: "name", key: "name" },
        { title: "City", dataIndex: "city", key: "city" },
        { title: "Capacity", dataIndex: "capacity", key: "capacity", render: (cap: number) => cap.toLocaleString() },
      ]}
      style={{ backgroundColor: CARD_BG }}
      className="dark-table"
    />
  </>
);

const HistorySection = () => (
  <>
    <SectionTitle icon={<HistoryOutlined style={{ color: ACCENT_COLOR }} />} title="World Cup History" />
    <Table
      dataSource={HISTORY}
      rowKey="year"
      columns={[
        { title: "Year", dataIndex: "year", key: "year" },
        { title: "Champion", dataIndex: "champion", key: "champion" },
        { title: "Runner", dataIndex: "runner", key: "runner" },
      ]}
      style={{ backgroundColor: CARD_BG }}
      className="dark-table"
    />
  </>
);

const FactsSection = () => (
  <>
    <SectionTitle icon={<BulbOutlined style={{ color: ACCENT_COLOR }} />} title="Facts" />
    <Row gutter={[12, 12]}>
      {FACTS.map((f, i) => (
        <Col xs={24} sm={12} lg={8} key={i}>
          <Card style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
            <Text style={{ color: TEXT_SECONDARY }}>{f.fact}</Text>
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

// Add this to your DATA section with other arrays
const FIXTURES = [
  // Opening Match - June 11
  { id: 1, date: 'June 11, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'A', home: 'Mexico', away: 'South Africa', homeFlag: '🇲🇽', awayFlag: '🇿🇦', venue: 'Estadio Azteca', city: 'Mexico City', status: 'upcoming' },
  { id: 2, date: 'June 11, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'A', home: 'South Korea', away: 'Czechia', homeFlag: '🇰🇷', awayFlag: '🇨🇿', venue: 'Estadio Akron', city: 'Guadalajara', status: 'upcoming' },
  
  // June 12
  { id: 3, date: 'June 12, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'B', home: 'Canada', away: 'Bosnia & Herzegovina', homeFlag: '🇨🇦', awayFlag: '🇧🇦', venue: 'BMO Field', city: 'Toronto', status: 'upcoming' },
  { id: 4, date: 'June 12, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'B', home: 'Qatar', away: 'Switzerland', homeFlag: '🇶🇦', awayFlag: '🇨🇭', venue: 'BC Place', city: 'Vancouver', status: 'upcoming' },
  { id: 5, date: 'June 12, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'C', home: 'USA', away: 'Türkiye', homeFlag: '🇺🇸', awayFlag: '🇹🇷', venue: 'SoFi Stadium', city: 'Los Angeles', status: 'upcoming' },
  
  // June 13
  { id: 6, date: 'June 13, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'C', home: 'Australia', away: 'Paraguay', homeFlag: '🇦🇺', awayFlag: '🇵🇾', venue: 'Lumen Field', city: 'Seattle', status: 'upcoming' },
  { id: 7, date: 'June 13, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'D', home: 'Spain', away: 'Brazil', homeFlag: '🇪🇸', awayFlag: '🇧🇷', venue: 'MetLife Stadium', city: 'East Rutherford', status: 'upcoming', isMarquee: true },
  { id: 8, date: 'June 13, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'D', home: 'Japan', away: 'Morocco', homeFlag: '🇯🇵', awayFlag: '🇲🇦', venue: 'AT&T Stadium', city: 'Dallas', status: 'upcoming' },
  
  // June 14
  { id: 9, date: 'June 14, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'E', home: 'Germany', away: 'Ecuador', homeFlag: '🇩🇪', awayFlag: '🇪🇨', venue: 'Mercedes-Benz Stadium', city: 'Atlanta', status: 'upcoming' },
  { id: 10, date: 'June 14, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'E', home: 'Ivory Coast', away: 'Curaçao', homeFlag: '🇨🇮', awayFlag: '🇨🇼', venue: 'Hard Rock Stadium', city: 'Miami', status: 'upcoming' },
  { id: 11, date: 'June 14, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'F', home: 'England', away: 'Netherlands', homeFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', awayFlag: '🇳🇱', venue: 'Arrowhead Stadium', city: 'Kansas City', status: 'upcoming', isMarquee: true },
  
  // June 15
  { id: 12, date: 'June 15, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'F', home: 'Venezuela', away: 'New Zealand', homeFlag: '🇻🇪', awayFlag: '🇳🇿', venue: 'NRG Stadium', city: 'Houston', status: 'upcoming' },
  { id: 13, date: 'June 15, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'G', home: 'Iran', away: 'Egypt', homeFlag: '🇮🇷', awayFlag: '🇪🇬', venue: 'Lincoln Financial Field', city: 'Philadelphia', status: 'upcoming' },
  { id: 14, date: 'June 15, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'G', home: 'Belgium', away: 'New Zealand', homeFlag: '🇧🇪', awayFlag: '🇳🇿', venue: 'Gillette Stadium', city: 'Boston', status: 'upcoming' },
  
  // June 16
  { id: 15, date: 'June 16, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'H', home: 'Portugal', away: 'Uruguay', homeFlag: '🇵🇹', awayFlag: '🇺🇾', venue: 'Levi Stadium', city: 'San Francisco', status: 'upcoming', isMarquee: true },
  { id: 16, date: 'June 16, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'H', home: 'Serbia', away: 'Saudi Arabia', homeFlag: '🇷🇸', awayFlag: '🇸🇦', venue: 'Estadio BBVA', city: 'Monterrey', status: 'upcoming' },
  { id: 17, date: 'June 16, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'I', home: 'France', away: 'Norway', homeFlag: '🇫🇷', awayFlag: '🇳🇴', venue: 'MetLife Stadium', city: 'East Rutherford', status: 'upcoming', isMarquee: true },
  
  // June 17
  { id: 18, date: 'June 17, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'I', home: 'Senegal', away: 'Iraq', homeFlag: '🇸🇳', awayFlag: '🇮🇶', venue: 'BMO Field', city: 'Toronto', status: 'upcoming' },
  { id: 19, date: 'June 17, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'J', home: 'Argentina', away: 'Algeria', homeFlag: '🇦🇷', awayFlag: '🇩🇿', venue: 'AT&T Stadium', city: 'Dallas', status: 'upcoming', isMarquee: true },
  { id: 20, date: 'June 17, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'J', home: 'Austria', away: 'Jordan', homeFlag: '🇦🇹', awayFlag: '🇯🇴', venue: 'SoFi Stadium', city: 'Los Angeles', status: 'upcoming' },
  
  // June 18
  { id: 21, date: 'June 18, 2026', time: '13:00 ET', stage: 'Group Stage', group: 'K', home: 'Portugal', away: 'Congo DR', homeFlag: '🇵🇹', awayFlag: '🇨🇩', venue: 'Mercedes-Benz Stadium', city: 'Atlanta', status: 'upcoming' },
  { id: 22, date: 'June 18, 2026', time: '16:00 ET', stage: 'Group Stage', group: 'K', home: 'Uzbekistan', away: 'Colombia', homeFlag: '🇺🇿', awayFlag: '🇨🇴', venue: 'Hard Rock Stadium', city: 'Miami', status: 'upcoming' },
  { id: 23, date: 'June 18, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'L', home: 'Croatia', away: 'Sweden', homeFlag: '🇭🇷', awayFlag: '🇸🇪', venue: 'Lumen Field', city: 'Seattle', status: 'upcoming' },
  { id: 24, date: 'June 18, 2026', time: '20:00 ET', stage: 'Group Stage', group: 'L', home: 'Ghana', away: 'Panama', homeFlag: '🇬🇭', awayFlag: '🇵🇦', venue: 'BC Place', city: 'Vancouver', status: 'upcoming' },

  // Round of 32 (Sample - July 4)
  { id: 25, date: 'July 4, 2026', time: '15:00 ET', stage: 'Round of 32', group: null, home: 'Winner A', away: '3rd Place B/C/D', homeFlag: '🏆', awayFlag: '🏆', venue: 'Estadio Azteca', city: 'Mexico City', status: 'upcoming' },
  { id: 26, date: 'July 4, 2026', time: '18:00 ET', stage: 'Round of 32', group: null, home: 'Winner B', away: '3rd Place A/C/D', homeFlag: '🏆', awayFlag: '🏆', venue: 'AT&T Stadium', city: 'Dallas', status: 'upcoming' },
  
  // Quarter-Finals (Sample)
  { id: 27, date: 'July 13, 2026', time: '15:00 ET', stage: 'Quarter-Finals', group: null, home: 'TBD', away: 'TBD', homeFlag: '❓', awayFlag: '❓', venue: 'AT&T Stadium', city: 'Dallas', status: 'upcoming' },
  { id: 28, date: 'July 13, 2026', time: '19:00 ET', stage: 'Quarter-Finals', group: null, home: 'TBD', away: 'TBD', homeFlag: '❓', awayFlag: '❓', venue: 'SoFi Stadium', city: 'Los Angeles', status: 'upcoming' },
  
  // Semi-Finals
  { id: 29, date: 'July 15, 2026', time: '16:00 ET', stage: 'Semi-Finals', group: null, home: 'TBD', away: 'TBD', homeFlag: '❓', awayFlag: '❓', venue: 'AT&T Stadium', city: 'Dallas', status: 'upcoming' },
  { id: 30, date: 'July 16, 2026', time: '16:00 ET', stage: 'Semi-Finals', group: null, home: 'TBD', away: 'TBD', homeFlag: '❓', awayFlag: '❓', venue: 'Mercedes-Benz Stadium', city: 'Atlanta', status: 'upcoming' },
  
  // Third Place
  { id: 31, date: 'July 18, 2026', time: '16:00 ET', stage: 'Third Place', group: null, home: 'Loser SF1', away: 'Loser SF2', homeFlag: '🥉', awayFlag: '🥉', venue: 'Hard Rock Stadium', city: 'Miami', status: 'upcoming' },
  
  // FINAL
  { id: 32, date: 'July 19, 2026', time: '18:00 ET', stage: 'Final', group: null, home: 'Winner SF1', away: 'Winner SF2', homeFlag: '🏆', awayFlag: '🏆', venue: 'MetLife Stadium', city: 'East Rutherford', status: 'upcoming', isFinal: true },
];

const STAGES_KNOCKOUT = ['Group Stage', 'Round of 32', 'Round of 16', 'Quarter-Finals', 'Semi-Finals', 'Third Place', 'Final'];

// Fixtures/Section Component - Add this to your SECTIONS object
const FixturesSection = () => {
  const [selectedStage, setSelectedStage] = useState('Group Stage');
  const [selectedDate, setSelectedDate] = useState('all');

  const stages = ['all', ...STAGES_KNOCKOUT];
  
  // Get unique dates for filter
  const dates = ['all', ...Array.from(new Set(FIXTURES.map(f => f.date)))];
  
  const filteredFixtures = FIXTURES.filter(fixture => {
    if (selectedStage !== 'all' && fixture.stage !== selectedStage) return false;
    if (selectedDate !== 'all' && fixture.date !== selectedDate) return false;
    return true;
  });

  // Group fixtures by date
  const groupedFixtures = filteredFixtures.reduce((acc, fixture) => {
    if (!acc[fixture.date]) acc[fixture.date] = [];
    acc[fixture.date].push(fixture);
    return acc;
  }, {} as Record<string, typeof FIXTURES>);

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      'Group Stage': 'blue',
      'Round of 32': 'cyan',
      'Round of 16': 'geekblue',
      'Quarter-Finals': 'purple',
      'Semi-Finals': 'orange',
      'Third Place': 'gold',
      'Final': 'red',
    };
    return colors[stage] || 'default';
  };

  return (
    <>
      <SectionTitle icon={<CalendarOutlined style={{ color: ACCENT_COLOR }} />} title="Fixtures & Schedule" />
      
      {/* Stage Filter */}
      <Card style={{ marginBottom: 20, backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ color: TEXT_PRIMARY, display: 'block', marginBottom: 8 }}>Tournament Stage</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {stages.map(stage => (
                <Tag.CheckableTag
                  key={stage}
                  checked={selectedStage === stage}
                  onChange={() => setSelectedStage(stage)}
                  style={{
                    backgroundColor: selectedStage === stage ? ACCENT_COLOR : 'transparent',
                    color: selectedStage === stage ? '#212121' : TEXT_SECONDARY,
                    borderColor: BORDER_COLOR,
                    padding: '6px 16px',
                    borderRadius: 20,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                  }}
                >
                  {stage === 'all' ? 'All Stages' : stage}
                </Tag.CheckableTag>
              ))}
            </div>
          </div>

          {/* Date Filter - Only show on larger screens or when needed */}
          <div>
            <Text strong style={{ color: TEXT_PRIMARY, display: 'block', marginBottom: 8 }}>Match Date</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {dates.map(date => (
                <Tag.CheckableTag
                  key={date}
                  checked={selectedDate === date}
                  onChange={() => setSelectedDate(date)}
                  style={{
                    backgroundColor: selectedDate === date ? ACCENT_COLOR : 'transparent',
                    color: selectedDate === date ? '#212121' : TEXT_SECONDARY,
                    borderColor: BORDER_COLOR,
                    padding: '4px 12px',
                    borderRadius: 20,
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  {date === 'all' ? 'All Dates' : date}
                </Tag.CheckableTag>
              ))}
            </div>
          </div>
        </Space>
      </Card>

      {/* Fixtures List */}
      {Object.entries(groupedFixtures).map(([date, fixtures]) => (
        <div key={date} style={{ marginBottom: 24 }}>
          <Card 
            size="small" 
            title={
              <Space>
                <CalendarOutlined style={{ color: ACCENT_COLOR }} />
                <Text strong style={{ color: TEXT_PRIMARY, fontSize: 16 }}>{date}</Text>
                <Badge count={fixtures.length} style={{ backgroundColor: ACCENT_COLOR }} />
              </Space>
            }
            style={{ backgroundColor: DARKER_BG, borderColor: BORDER_COLOR }}
            headStyle={{ borderBottomColor: BORDER_COLOR }}
          >
            <Row gutter={[16, 16]}>
              {fixtures.map((fixture) => (
                <Col xs={24} md={12} lg={8} key={fixture.id}>
                  <Card 
                    size="small"
                    style={{ 
                      backgroundColor: CARD_BG, 
                      borderColor: BORDER_COLOR,
                      borderLeft: `4px solid ${fixture.isMarquee ? ACCENT_COLOR : getStageColor(fixture.stage)}`,
                    }}
                    hoverable
                  >
                    {/* Stage Badge */}
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tag color={getStageColor(fixture.stage)} style={{ fontSize: 11 }}>
                        {fixture.stage}
                      </Tag>
                      {fixture.group && (
                        <Tag style={{ backgroundColor: '#3a3a3a', borderColor: BORDER_COLOR, color: TEXT_SECONDARY }}>
                          Group {fixture.group}
                        </Tag>
                      )}
                      {fixture.isMarquee && (
                        <Tag color="gold" style={{ fontSize: 10 }}>⭐ MARQUEE</Tag>
                      )}
                      {fixture.isFinal && (
                        <Tag color="red" style={{ fontSize: 10 }}>🏆 FINAL</Tag>
                      )}
                    </div>

                    {/* Teams */}
                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 32, marginBottom: 4 }}>{fixture.homeFlag}</div>
                          <Text strong style={{ color: TEXT_PRIMARY, fontSize: 14 }}>{fixture.home}</Text>
                        </div>
                        <Text strong style={{ color: ACCENT_COLOR, fontSize: 18 }}>VS</Text>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 32, marginBottom: 4 }}>{fixture.awayFlag}</div>
                          <Text strong style={{ color: TEXT_PRIMARY, fontSize: 14 }}>{fixture.away}</Text>
                        </div>
                      </div>
                    </div>

                    <Divider style={{ margin: '8px 0', borderColor: BORDER_COLOR }} />

                    {/* Match Details */}
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FieldTimeOutlined style={{ color: ACCENT_COLOR }} />
                        <Text style={{ color: TEXT_SECONDARY, fontSize: 12 }}>{fixture.time} ET</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <EnvironmentOutlined style={{ color: ACCENT_COLOR }} />
                        <Text style={{ color: TEXT_SECONDARY, fontSize: 12 }}>{fixture.venue}, {fixture.city}</Text>
                      </div>
                    </Space>

                    {/* Live/Upcoming Badge */}
                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                      {fixture.status === 'live' ? (
                        <Tag color="red" icon={<div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'red', animation: 'pulse 1s infinite' }} />}>
                          LIVE NOW
                        </Tag>
                      ) : (
                        <Tag style={{ backgroundColor: '#3a3a3a', borderColor: BORDER_COLOR, color: TEXT_SECONDARY }}>
                          Upcoming
                        </Tag>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      ))}

      {/* Key Match Highlights */}
      <Card 
        title={
          <Space>
            <StarOutlined style={{ color: ACCENT_COLOR }} />
            <Text strong style={{ color: TEXT_PRIMARY }}>Must-Watch Matches</Text>
          </Space>
        }
        style={{ backgroundColor: DARKER_BG, borderColor: BORDER_COLOR, marginTop: 24 }}
        headStyle={{ borderBottomColor: BORDER_COLOR }}
      >
        <Row gutter={[16, 16]}>
          {FIXTURES.filter(f => f.isMarquee || f.isFinal).map((fixture) => (
            <Col xs={24} sm={12} key={fixture.id}>
              <Card size="small" style={{ backgroundColor: CARD_BG, borderColor: BORDER_COLOR }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong style={{ color: ACCENT_COLOR }}>{fixture.date}</Text>
                  <Text strong style={{ color: TEXT_PRIMARY, fontSize: 16 }}>
                    {fixture.homeFlag} {fixture.home} vs {fixture.away} {fixture.awayFlag}
                  </Text>
                  <Text style={{ color: TEXT_SECONDARY }}>{fixture.venue}, {fixture.city}</Text>
                  <Text style={{ color: TEXT_SECONDARY }}>{fixture.time} ET</Text>
                  {fixture.isFinal && (
                    <Tag color="red" style={{ marginTop: 4 }}>🏆 WORLD CUP FINAL 2026</Tag>
                  )}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Add pulse animation for live indicator */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}
      </style>
    </>
  );
};

// Add this to your SECTIONS object:
const SECTIONS = {
  overview: <OverviewSection />,
  fixtures: <FixturesSection />,  // Add this line
  groups: <GroupsSection />,
  players: <PlayersSection />,
  teams: <TeamsSection />,
  stadiums: <StadiumsSection />,
  history: <HistorySection />,
  facts: <FactsSection />,
  rivalries: <RivalriesSection />,
  champions: <ChampionsSection />,
  stages: <StagesSection />,
};

const FIFA2026App = () => {
  const [section, setSection] = useState("overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const menuItems = Object.keys(SECTIONS).map((key) => ({
    key,
    label: key.toUpperCase(),
  }));

  const menuComponent = (
    <Menu
      mode="inline"
      selectedKeys={[section]}
      onClick={(e) => {
        setSection(e.key);
        setMobileMenuOpen(false);
      }}
      items={menuItems}
      style={{ 
        background: DARKER_BG, 
        color: TEXT_SECONDARY,
        borderRight: 'none',
      }}
      className="dark-menu"
    />
  );

  // Global styles for dark theme
  const darkThemeStyles = `
    .dark-table .ant-table {
      background-color: ${CARD_BG} !important;
      color: ${TEXT_SECONDARY} !important;
    }
    .dark-table .ant-table-thead > tr > th {
      background-color: ${DARKER_BG} !important;
      color: ${TEXT_PRIMARY} !important;
      border-bottom-color: ${BORDER_COLOR} !important;
    }
    .dark-table .ant-table-tbody > tr > td {
      border-bottom-color: ${BORDER_COLOR} !important;
      color: ${TEXT_SECONDARY} !important;
    }
    .dark-table .ant-table-tbody > tr:hover > td {
      background-color: #3a3a3a !important;
    }
    .dark-menu .ant-menu-item {
      color: ${TEXT_SECONDARY} !important;
    }
    .dark-menu .ant-menu-item-selected {
      background-color: #3a3a3a !important;
      color: ${ACCENT_COLOR} !important;
    }
    .dark-menu .ant-menu-item-active {
      background-color: #333333 !important;
      color: ${ACCENT_COLOR} !important;
    }
    .ant-drawer-content {
      background-color: ${DARKER_BG} !important;
    }
    .ant-drawer-header {
      background-color: ${DARKER_BG} !important;
      border-bottom-color: ${BORDER_COLOR} !important;
    }
    .ant-drawer-title {
      color: ${TEXT_PRIMARY} !important;
    }
    .ant-timeline-item-content {
      color: ${TEXT_SECONDARY} !important;
    }
    .ant-timeline-item-tail {
      border-left-color: ${BORDER_COLOR} !important;
    }
    .ant-timeline-item-head {
      background-color: ${ACCENT_COLOR} !important;
    }
    .ant-statistic-content {
      color: ${ACCENT_COLOR} !important;
    }
    .ant-progress-text {
      color: ${TEXT_SECONDARY} !important;
    }
    .ant-progress-inner {
      background-color: #3a3a3a !important;
    }
    .ant-rate-star:not(.ant-rate-star-full) .ant-rate-star-second,
    .ant-rate-star .ant-rate-star-first, 
    .ant-rate-star .ant-rate-star-second {
      color: #5a5a5a !important;
    }
    .ant-rate-star-full .ant-rate-star-first, 
    .ant-rate-star-full .ant-rate-star-second {
      color: ${ACCENT_COLOR} !important;
    }
    body {
      background-color: ${DARK_BG} !important;
    }
  `;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgBase: DARK_BG,
          colorTextBase: TEXT_PRIMARY,
          colorBorder: BORDER_COLOR,
        },
        components: {
          Card: {
            colorBgContainer: CARD_BG,
          },
          Menu: {
            colorBgContainer: DARKER_BG,
            colorItemText: TEXT_SECONDARY,
            colorItemTextSelected: ACCENT_COLOR,
            colorItemBgSelected: '#3a3a3a',
          },
        },
      }}
    >
      <style>{darkThemeStyles}</style>
      <Layout style={{ minHeight: "100vh", background: DARK_BG }}>

        <Layout style={{ background: DARK_BG }}>
          {/* Desktop Sider */}
          {!isMobile && (
            <Sider 
              width={250} 
              style={{ 
                background: DARKER_BG, 
                borderRight: `1px solid ${BORDER_COLOR}`,
                position: 'sticky',
                top: 0,
                height: '100vh',
                overflow: 'auto',
              }}
            >
              {menuComponent}
            </Sider>
          )}

          {/* Mobile Drawer */}
          <Drawer
            title="FIFA World Cup 2026"
            placement="left"
            onClose={() => setMobileMenuOpen(false)}
            open={mobileMenuOpen}
            styles={{ body: { padding: 0, background: DARKER_BG } }}
            headerStyle={{ background: DARKER_BG, borderBottomColor: BORDER_COLOR }}
          >
            {menuComponent}
          </Drawer>

          <Content style={{ padding: isMobile ? '12px' : '20px', overflowX: 'hidden' }}>
            {SECTIONS[section as keyof typeof SECTIONS]}
          </Content>
        </Layout>

        {/* Mobile Menu Button */}
        {isMobile && (
          <Button
            type="primary"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              borderRadius: '50%',
              width: 56,
              height: 56,
              backgroundColor: ACCENT_COLOR,
              borderColor: ACCENT_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          />
        )}
      </Layout>
    </ConfigProvider>
  );
};

export default FIFA2026App;